var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/no-explicit-any */
import { matches } from '@js/modules/utilities';
import { i18n, setLocale } from '@vuedir/plugins/i18n/i18n';
/**
 * Dynamically loads scripts based on what elements are present in the DOM.
 *
 * This eliminates the need for different webpack entrypoints, as modules are
 * only (async) loaded when there are elements present that require that module.
 *
 * Since scripts are cached, this basically means that all our JS is loaded
 * progressively when needed and resolves immediately on subsequent requests.
 */
window.addEventListener('DOMContentLoaded', () => __awaiter(void 0, void 0, void 0, function* () {
    require('@js/polyfills');
    yield setLocale();
    loadScripts();
    loadApps();
}));
/**
 * Loads modules.
 */
export const loadScripts = () => {
    // Modules loaded on virtually every page.
    loadAnalyticsModule();
    loadNavigationModule();
    loadErrorHandlerModule();
    loadFormValidationModule();
    // Modules loaded sporadically.
    loadToggleModule();
    loadListenerModule();
    loadTableModule();
    loadSliderModule();
    loadButtonModule();
    loadSuperButtonModule();
    loadLineitemModule();
    loadAutoSubmitModule();
    loadShippingAndBillingModule();
    loadGoogleAutocompleteModule();
    loadPaymentOptionsModule();
    loadPaymentButtonsModule();
    loadProofsModule();
    loadFilterModule();
    loadWxCheckoutModule();
    loadCharacterCountModule();
    loadFormSubmitModule();
    loadHotwiredTurboModule();
    // Apps to be remounted on subsequent turbo page loads
    document.documentElement.addEventListener('turbo:load', () => {
        loadToggleModule();
        loadFilterModule();
    });
};
/**
 * Loads Vue apps.
 */
export const loadApps = () => {
    loadClipboardApp();
    loadRegionSwitcherApp();
    loadLoginApp();
    loadGiftcardApp();
    loadInspirationApp();
    loadBuilderTrigger();
    loadProofReview3dApp();
    loadLazyLoaderApp();
    loadWordArtApp();
};
/**
 * Loads the Google Analytics module.
 *
 * We obfuscate this chunkname in order to not trip up overzealous ad blockers.
 */
const loadAnalyticsModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const { default: Analytics } = yield import(/* webpackChunkName: 'global' */ '@js/modules/Analytics');
    new Analytics();
});
/**
 * Loads the Navigation module.
 */
const loadNavigationModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const { default: Navigation } = yield import(/* webpackChunkName: 'global' */ '@js/modules/Navigation');
    new Navigation();
});
/**
 * Loads the ErrorHandler module.
 */
const loadErrorHandlerModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const { default: ErrorHandler } = yield import(/* webpackChunkName: 'global' */ '@js/modules/ErrorHandler');
    new ErrorHandler();
});
/**
 * Loads the FormValidation module.
 */
const loadFormValidationModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const { default: FormValidation } = yield import(/* webpackChunkName: 'global' */ '@js/modules/FormValidation');
    new FormValidation();
});
/**
 * Finds elements with the data-module-toggle attribute and attaches the Toggle
 * module to them.
 */
const loadToggleModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-module-toggle]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { default: Toggle } = yield import(/* webpackChunkName: 'Toggle' */ '@js/modules/Toggle');
        new Toggle(element);
    }));
});
const loadListenerModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-module-listener]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { default: Listener } = yield import(/* webpackChunkName: 'Listener' */ '@js/modules/Listener');
        new Listener(element);
    }));
});
/**
 * Finds elements with the data-module-filter attribute and attaches the Filter
 * module to them.
 */
const loadFilterModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-module-filter]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { default: Filter } = yield import(/* webpackChunkName: 'Filter' */ '@js/modules/Filter');
        const customPathAttribute = element.getAttribute('data-custom-path');
        const customPath = customPathAttribute ? customPathAttribute : '';
        new Filter(element, customPath);
    }));
});
/**
 * Finds elements with the data-module-table attribute and attaches the Table
 * module to them.
 */
const loadTableModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-module-table]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { default: Table } = yield import(/* webpackChunkName: 'Table' */ '@js/modules/Table');
        new Table(element);
    }));
});
/**
 * Finds elements with the data-module-slider attribute and attaches the Slider
 * module to them.
 */
const loadSliderModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-module-slider]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { default: Slider } = yield import(/* webpackChunkName: 'Slider' */ '@js/modules/Slider');
        new Slider(element);
    }));
});
/**
 * Finds elements with the data-module-loading-button attribute and attaches the
 * Button module to them.
 */
const loadButtonModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-module-loading-button]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { default: Button } = yield import(/* webpackChunkName: 'Button' */ '@js/modules/Button');
        new Button(element);
    }));
});
/**
 * Finds elements with the data-module-lineitem attribute and attaches the
 * Lineitem module to them.
 */
const loadLineitemModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-module-lineitem]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { default: Lineitem } = yield import(/* webpackChunkName: 'Lineitem' */ '@js/modules/Lineitem');
        new Lineitem(element);
    }));
});
/**
 * Finds elements with the data-module-lineitem attribute and attaches the
 * Lineitem module to them.
 */
const loadSuperButtonModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-module-superbutton]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { default: SuperButton } = yield import(/* webpackChunkName: 'SuperButton' */ '@js/modules/SuperButton');
        new SuperButton(element);
    }));
});
/**
 * Finds elements with the data-module-auto-submit attribute and attaches the
 * AutoSubmit module to them.
 */
const loadAutoSubmitModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-module-auto-submit]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { default: AutoSubmit } = yield import(/* webpackChunkName: 'AutoSubmit' */ '@js/modules/AutoSubmit');
        const option = element.getAttribute('[data-module-auto-submit]') || 'change';
        const options = {
            event: option,
        };
        new AutoSubmit(element, options);
    }));
});
/**
 * Finds elements with the data-module-shipping-and-billing attribute and loads
 * the ShippingAndBilling module.
 */
const loadShippingAndBillingModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const element = document.querySelector('[data-module-shipping-and-billing]');
    if (element) {
        const { default: ShippingAndBilling } = yield import(/* webpackChunkName: 'ShippingAndBilling' */ '@js/modules/ShippingAndBilling');
        new ShippingAndBilling();
    }
});
/**
 * Finds elements with the data-module-google-autocomplete attribute and loads
 * the GoogleAutocomplete module.
 */
const loadGoogleAutocompleteModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-module-google-autocomplete]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { default: GoogleAutocomplete } = yield import(/* webpackChunkName: 'GoogleAutocomplete' */ '@js/modules/GoogleAutocomplete');
        const selector = element.getAttribute('data-module-google-autocomplete') || 'shipping';
        new GoogleAutocomplete(element, selector);
    }));
});
/**
 * Finds elements with the data-module-payment-options attribute and loads the
 * PaymentOptions module.
 */
const loadPaymentOptionsModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const element = document.querySelector('[data-module-payment-options]');
    if (element) {
        const { default: PaymentOptions } = yield import(/* webpackChunkName: 'PaymentOptions' */ '@js/modules/PaymentOptions');
        new PaymentOptions();
    }
});
/**
 * Finds elements with the data-module-payment-buttons attribute and loads the
 * PaymentButtons module.
 */
const loadPaymentButtonsModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const element = document.querySelector('[data-module-payment-buttons]');
    if (element) {
        const { default: PaymentButtons } = yield import(/* webpackChunkName: 'PaymentButtons' */ '@js/modules/PaymentButtons');
        new PaymentButtons();
    }
});
/**
 * Finds elements with the data-module-proofs attribute and loads the Proofs
 * module.
 */
const loadProofsModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const element = document.querySelector('[data-module-proofs]');
    if (element) {
        const { default: Proofs } = yield import(/* webpackChunkName: 'Proofs' */ '@js/modules/Proofs');
        new Proofs();
    }
});
/**
 * Finds elements with the data-module-wx-checkout attribute and loads the
 * WxCheckout module.
 */
const loadWxCheckoutModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const element = document.querySelector('[data-module-wx-checkout]');
    if (element) {
        const { default: WxCheckout } = yield import(/* webpackChunkName: 'WxCheckout' */ '@js/modules/WxCheckout');
        new WxCheckout();
    }
});
/**
 * Finds elements with the data-module-character-count attribute and attaches the
 * CharacterCount module to them.
 */
const loadCharacterCountModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-module-charactercount]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { default: CharacterCount } = yield import(/* webpackChunkName: 'CharacterCount' */ '@js/modules/CharacterCount');
        const option = element.getAttribute('[data-module-charactercount]') || 'change';
        const options = {
            event: option,
        };
        new CharacterCount(element);
    }));
});
/**
 * Finds forms with the data-module-form-submit and overrides the form submit.
 * Turning it into an axios request.
 */
const loadFormSubmitModule = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-module-form-submit]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { default: FormSubmit } = yield import(/* webpackChunkName: 'FormSubmit' */ '@js/modules/FormSubmit');
        const option = element.getAttribute('[data-module-form-submit]') || 'change';
        const options = {
            event: option,
        };
        new FormSubmit(element);
    }));
});
/**
 * Finds elements with the data-module-hotwired-turbo attribute and imports the
 * hotwiredTurbo module into the page
 */
const loadHotwiredTurboModule = (renewed) => __awaiter(void 0, void 0, void 0, function* () {
    document.addEventListener('click', (event) => {
        const target = event.target;
        const match = matches(target, '[data-turbo-trigger]');
        if (match) {
            event.preventDefault();
            // Only load when a hotwired component is triggered via mouseEvent
            import(/* webpackChunkName: 'hotwiredTurbo' */ '@hotwired/turbo').then((turbo) => __awaiter(void 0, void 0, void 0, function* () {
                const anchor = target.closest('a');
                if (anchor) {
                    turbo.visit(anchor.href);
                    const scrollTo = () => {
                        const scrollAnchor = document.getElementById('turboScrollAnchor');
                        if (scrollAnchor) {
                            const header = document.getElementById('header');
                            if (header) {
                                const topOffset = header.offsetHeight; // account for fixed cp header
                                window.scroll({
                                    top: scrollAnchor.offsetTop - topOffset,
                                });
                            }
                        }
                        document.removeEventListener('turbo:render', scrollTo);
                    };
                    document.addEventListener('turbo:render', scrollTo);
                }
            }));
        }
    });
});
/**
 * Finds elements with the data-app-clipboard attribute and attaches the
 * Clipboard Vue app to them.
 */
const loadClipboardApp = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-app-clipboard]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { createApp, resolveComponent, defineAsyncComponent, h } = yield import(/* webpackChunkName: 'vue' */ 'vue');
        const classes = element.getAttribute('class');
        const props = JSON.parse(element.getAttribute('data-app-props'));
        let app = createApp({
            el: element,
            components: {
                'clipboardCopy': defineAsyncComponent(() => import(/* webpackChunkName: "ClipboardCopy" */ '@vuedir/components/ClipboardCopy')),
            },
            render: () => {
                const component = resolveComponent('clipboardCopy');
                return h(component, {
                    class: classes,
                });
            },
        }, props);
        app.mount(element);
    }));
});
const loadRegionSwitcherApp = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-app-region-switcher]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { createApp, defineAsyncComponent, resolveComponent, h } = yield import(/* webpackChunkName: 'vue' */ 'vue');
        const classes = element.getAttribute('class');
        const props = JSON.parse(element.getAttribute('data-app-props'));
        const app = createApp({
            el: element,
            components: {
                'regionSwitcher': defineAsyncComponent(() => import(/* webpackChunkName: "RegionSwitcher" */ '@vuedir/components/RegionSwitcher')),
            },
            render: () => {
                const regionSwitcher = resolveComponent('regionSwitcher');
                return h(regionSwitcher, {
                    class: classes,
                });
            },
        }, props);
        app.use(i18n);
        app.mount(element);
    }));
});
/**
 * Finds elements with the data-app-lazy-art-image attribute and attaches the
 * Lazy Image Vue app to them.
 */
const loadLazyLoaderApp = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-app-lazy-art-image]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { createApp, resolveComponent, h, defineAsyncComponent } = yield import(/* webpackChunkName: 'vue' */ 'vue');
        const classes = element.getAttribute('class');
        const props = JSON.parse(element.getAttribute('data-app-props'));
        let app = createApp({
            el: element,
            components: {
                'lazyArtImage': defineAsyncComponent(() => import(/* webpackChunkName: "LazyArtImage" */ '@vuedir/components/LazyArtImage')),
            },
            render: () => {
                const component = resolveComponent('lazyArtImage');
                return h(component, {
                    class: classes,
                });
            },
        }, props);
        setTurbolinkListener(app);
        app.mount(element);
    }));
});
/**
 * Finds elements with the data-app-notification attribute and attaches the Notification Vue app
 * to them.
 */
const loadNotificationApp = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-app-notification]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { createApp, resolveComponent, h, defineAsyncComponent } = yield import(/* webpackChunkName: 'vue' */ 'vue');
        const classes = element.getAttribute('class');
        const props = JSON.parse(element.getAttribute('data-app-props'));
        let app = createApp({
            el: element,
            components: {
                'Notification': defineAsyncComponent(() => import(/* webpackChunkName: "Notification" */ '@vuedir/components/Notification')),
            },
            render: () => {
                const component = resolveComponent('notification');
                return h(component, {
                    class: classes,
                });
            },
        }, props);
        app.use(i18n);
        app.mount(element);
    }));
});
/**
 * Finds elements with the data-app-login attribute and attaches the Login Vue app
 * to them.
 */
const loadLoginApp = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-app-login]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { createApp, resolveComponent, h, defineAsyncComponent } = yield import(/* webpackChunkName: 'vue' */ 'vue');
        const classes = element.getAttribute('class');
        const props = JSON.parse(element.getAttribute('data-app-props'));
        let app = createApp({
            el: element,
            components: {
                'Login': defineAsyncComponent(() => import(/* webpackChunkName: "Login" */ '@vuedir/components/Login')),
            },
            render: () => {
                const component = resolveComponent('login');
                return h(component, {
                    class: classes,
                });
            },
        }, props);
        app.use(i18n);
        app.mount(element);
    }));
});
/**
 * Finds elements with the data-app-inspiration attribute and attaches the
 * Inspiration Vue app to them.
 */
const loadInspirationApp = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-app-inspiration]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { observeApp } = yield import(/* webpackChunkName: 'utilities' */ '@js/modules/utilities');
        observeApp(element, {}, () => __awaiter(void 0, void 0, void 0, function* () {
            const { createApp, resolveComponent, h, defineAsyncComponent } = yield import(/* webpackChunkName: 'vue' */ 'vue');
            const classes = element.getAttribute('class');
            const props = JSON.parse(element.getAttribute('data-app-props'));
            let app = createApp({
                el: element,
                components: {
                    'inspiration': defineAsyncComponent(() => import(/* webpackChunkName: "Inspiration" */ '@vuedir/components/Inspiration')),
                },
                render: () => {
                    const inspirationComponent = resolveComponent('inspiration');
                    return h(inspirationComponent, {
                        class: classes,
                    });
                },
            }, props);
            app.use(i18n);
            app.mount(element);
        }));
    }));
});
/**
 * Finds elements with the data-app-giftcard attribute and attaches the Giftcard
 * Vue app to them.
 */
const loadGiftcardApp = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-app-giftcard]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { observeApp } = yield import(/* webpackChunkName: 'utilities' */ '@js/modules/utilities');
        observeApp(element, {}, () => __awaiter(void 0, void 0, void 0, function* () {
            const { createApp, resolveComponent, h, defineAsyncComponent } = yield import(/* webpackChunkName: 'vue' */ 'vue');
            const localesPromise = import(/* webpackChunkName: 'locales' */ '@vuedir/constants/locales');
            const currencyFilterPromise = import(/* webpackChunkName: 'CurrencyFilter' */ '@vuedir/filters/Currency');
            const locales = yield localesPromise;
            const { default: CurrencyFilter } = yield currencyFilterPromise;
            const classes = element.getAttribute('class');
            const props = JSON.parse(element.getAttribute('data-app-props'));
            const locale = props.locale || locales.LOCALE_DEFAULT;
            const currencyCode = props.currencyCode || locales.CURRENCY_CODE_DEFAULT;
            const localeUs = locales.LOCALE_DEFAULT;
            const currencyCodeUs = locales.CURRENCY_CODE_DEFAULT;
            const currencyFilter = new CurrencyFilter(locale, currencyCode);
            const currencyFilterUs = new CurrencyFilter(localeUs, currencyCodeUs);
            let app = createApp({
                el: element,
                components: {
                    'giftcard': defineAsyncComponent(() => import(/* webpackChunkName: "Giftcard" */ '@vuedir/components/Giftcard')),
                },
                render: () => {
                    const component = resolveComponent('giftcard');
                    return h(component, {
                        class: classes,
                    });
                },
            }, props);
            app.config.globalProperties.$filters = {
                currency(value) {
                    return currencyFilter.format(value);
                },
                currencyUS(value) {
                    return currencyFilterUs.format(value);
                }
            };
            app.use(i18n);
            app.mount(element);
        }));
    }));
});
/**
 * Finds elements with the data-app-reviews attribute and attaches the Reviews
 * Vue app to them.
 */
const loadReviewsApp = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-app-reviews]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { createApp, resolveComponent, h, defineAsyncComponent } = yield import(/* webpackChunkName: 'vue' */ 'vue');
        const classes = element.getAttribute('class');
        const props = JSON.parse(element.getAttribute('data-app-props'));
        let app = createApp({
            el: element,
            components: {
                'reviews': defineAsyncComponent(() => import(/* webpackChunkName: "Reviews" */ '@vuedir/components/Reviews')),
            },
            render: () => {
                const component = resolveComponent('reviews');
                return h(component, {
                    class: classes,
                });
            }
        }, props);
        app.use(i18n);
        app.mount(element);
    }));
});
const loadWordArtApp = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-app-wordart]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { createApp, resolveComponent, h, defineAsyncComponent } = yield import(/* webpackChunkName: 'vue' */ 'vue');
        const classes = element.getAttribute('class');
        const localesPromise = import(/* webpackChunkName: 'locales' */ '@vuedir/constants/locales');
        const currencyFilterPromise = import(/* webpackChunkName: 'CurrencyFilter' */ '@vuedir/filters/Currency');
        const props = JSON.parse(element.getAttribute('data-app-props'));
        const locales = yield localesPromise;
        const currencyCode = props.currencyCode || locales.CURRENCY_CODE_DEFAULT;
        const { default: CurrencyFilter } = yield currencyFilterPromise;
        const locale = props.locale || locales.LOCALE_DEFAULT;
        const currencyFilter = new CurrencyFilter(locale, currencyCode);
        // Vue.filter('currency', (value: number) => currencyFilter.format(value))
        let app = createApp({
            el: element,
            components: {
                'wordart': defineAsyncComponent(() => import(/* webpackChunkName: "WordArt" */ '@vuedir/components/WordArt')),
            },
            render: () => {
                const component = resolveComponent('wordart');
                return h(component, {
                    class: classes,
                });
            }
        }, props);
        app.config.globalProperties.$filters = {
            currency(value) {
                return currencyFilter.format(value);
            }
        };
        app.use(i18n);
        app.mount(element);
    }));
});
const loadBuilderTrigger = () => __awaiter(void 0, void 0, void 0, function* () {
    if (window.location.href.indexOf('create') != -1 || window.location.href.indexOf('modify') != -1 || (window.location.href.indexOf('products') !== -1 && window.location.href.indexOf('open=true') != -1)) {
        // If the url includes "create" or "modify" on product pages,
        // launch the builder right away
        loadBuilderApp();
        loadTileBuilderApp();
    }
    else if (window.location.href.indexOf('open=true') != -1 && window.location.pathname === '/') {
        // if open=true on the homepage redirect to the default builder with all the search params
        const url = window.location.origin + '/products/canvas-prints/create' + window.location.search;
        window.location.replace(url);
    }
    else {
        // Add an event listener to the CTA
        const builderAttribute = document.querySelector('[data-app-builder]') || document.querySelector('[data-app-tile-builder]');
        if (builderAttribute) {
            const triggerBuilderRoute = (event) => {
                const target = event.target;
                const match = matches(target, '[data-app-launch-builder]');
                if (match) {
                    let href = match.getAttribute('href');
                    // support the use of valid urls
                    if (href.startsWith('/art')) {
                        href = href.replace('/art', '');
                    }
                    event.preventDefault();
                    const hasTileAttribute = document.querySelector('[data-app-tile-builder]');
                    if (hasTileAttribute) {
                        loadTileBuilderApp(href);
                    }
                    else {
                        loadBuilderApp(href);
                    }
                }
            };
            document.addEventListener('click', triggerBuilderRoute);
        }
    }
});
/**
 * Finds elements with the data-app-builder attribute and attaches the Builder
 * Vue app to them.
 */
const loadBuilderApp = (lazyHref) => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-app-builder]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { createApp, resolveComponent, h } = yield import(/* webpackChunkName: 'vue' */ 'vue');
        const vuexPromise = import(/* webpackChunkName: 'vuex' */ 'vuex');
        const vueRouterPromise = import(/* webpackChunkName: 'vue-router' */ 'vue-router');
        const currencyFilterPromise = import(/* webpackChunkName: 'CurrencyFilter' */ '@vuedir/filters/Currency');
        const localesPromise = import(/* webpackChunkName: 'locales' */ '@vuedir/constants/locales');
        const routesPromise = import(/* webpackChunkName: 'routes' */ '@vuedir/constants/routes');
        const scoopsPromise = import(/* webpackChunkName: 'scoops' */ '@canvaspop/scoops');
        const { createStore } = yield vuexPromise;
        const { createRouter, createWebHistory } = yield vueRouterPromise;
        const { default: CurrencyFilter } = yield currencyFilterPromise;
        const locales = yield localesPromise;
        const routes = yield routesPromise;
        let modules;
        modules = yield scoopsPromise.then((scoops) => { return scoops.store; });
        const routerRoutes = [
            {
                path: '/', component: () => import(/* webpackChunkName: "App" */ '@vuedir/components/App'),
                children: [
                    { path: 'create', name: routes.ROUTE_CREATE, component: () => import(/* webpackChunkName: "Builder" */ '@vuedir/components/Builder') },
                    { path: 'modify/:id', name: routes.ROUTE_MODIFY, component: () => import(/* webpackChunkName: "Builder" */ '@vuedir/components/Builder') },
                ],
            },
        ];
        const router = createRouter({
            history: createWebHistory(element.getAttribute('data-app-builder')),
            routes: routerRoutes,
        });
        const store = createStore({ modules });
        const props = JSON.parse(element.getAttribute('data-app-props'));
        const locale = props.locale || locales.LOCALE_DEFAULT;
        const currencyCode = props.currencyCode || locales.CURRENCY_CODE_DEFAULT;
        const currencyFilter = new CurrencyFilter(locale, currencyCode);
        const triggerBuilderRoute = (event) => {
            const target = event.target;
            const match = matches(target, '[data-app-launch-builder]');
            if (match) {
                let href = match.getAttribute('href');
                // support the use of valid urls
                if (href.startsWith('/art')) {
                    href = href.replace('/art', '');
                }
                event.preventDefault();
                router.push(href);
            }
        };
        let app = createApp({
            el: element,
            mounted: function () {
                // Make it possible for regular buttons to trigger the router.
                if (!lazyHref) {
                    document.addEventListener('click', triggerBuilderRoute);
                }
            },
            unmounted: function () {
                // Remove obsolete event listener
                if (!lazyHref) {
                    document.removeEventListener('click', triggerBuilderRoute);
                }
            },
            render: () => {
                const component = resolveComponent('router-view');
                return h(component);
            },
        }, props);
        app.config.globalProperties.$filters = {
            currency(value) {
                return currencyFilter.format(value);
            }
        };
        app.use(router);
        app.use(store);
        app.use(i18n);
        setTurbolinkListener(app);
        app.mount(element);
        if (lazyHref) {
            router.push(lazyHref);
        }
    }));
});
/**
 * Finds elements with the data-app-proof-review-3d attribute and attaches the ProofReview3d
 * Vue app to them.
 */
const loadProofReview3dApp = () => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-app-proof-review-3d]');
    const scoopsPromise = import(/* webpackChunkName: 'scoops' */ '@canvaspop/scoops');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { createApp, resolveComponent, h, defineAsyncComponent } = yield import(/* webpackChunkName: 'vue' */ 'vue');
        const { createStore } = yield import(/* webpackChunkName: 'vuex' */ 'vuex');
        const classes = element.getAttribute('class');
        const props = JSON.parse(element.getAttribute('data-app-props'));
        let modules;
        modules = yield scoopsPromise.then((scoops) => { return scoops.store; });
        props.orderProofs = Object.values(props.orderProofs);
        props.orderProofs.forEach((proof) => {
            if (proof.notes) {
                proof.notes = Object.values(proof.notes);
            }
        });
        const store = createStore({ modules });
        let app = createApp({
            el: element,
            components: {
                'proofReview3d': defineAsyncComponent(() => import(/* webpackChunkName: "ProofReview3d" */ '@vuedir/components/ProofReview3d')),
            },
            render: () => {
                const component = resolveComponent('proofReview3d');
                return h(component, {
                    class: classes,
                });
            },
        }, props);
        app.use(store);
        app.use(i18n);
        app.mount(element);
    }));
});
/**
 * Finds elements with the data-app-builder attribute and attaches the Builder
 * Vue app to them.
 */
const loadTileBuilderApp = (lazyHref) => __awaiter(void 0, void 0, void 0, function* () {
    const elements = document.querySelectorAll('[data-app-tile-builder]');
    elements.forEach((element) => __awaiter(void 0, void 0, void 0, function* () {
        const { createApp, resolveComponent, h } = yield import(/* webpackChunkName: 'vue' */ 'vue');
        const vuexPromise = import(/* webpackChunkName: 'vuex' */ 'vuex');
        const vueRouterPromise = import(/* webpackChunkName: 'vue-router' */ 'vue-router');
        const currencyFilterPromise = import(/* webpackChunkName: 'CurrencyFilter' */ '@vuedir/filters/Currency');
        const localesPromise = import(/* webpackChunkName: 'locales' */ '@vuedir/constants/locales');
        const routesPromise = import(/* webpackChunkName: 'routes' */ '@vuedir/constants/routes');
        const scoopsPromise = import(/* webpackChunkName: 'scoops' */ '@canvaspop/scoops');
        const { createStore } = yield vuexPromise;
        const { createRouter, createWebHistory } = yield vueRouterPromise;
        const { default: CurrencyFilter } = yield currencyFilterPromise;
        const locales = yield localesPromise;
        const routes = yield routesPromise;
        let modules;
        modules = yield scoopsPromise.then((scoops) => { return scoops.store; });
        const routerRoutes = [
            {
                path: '/', component: () => import(/* webpackChunkName: "App" */ '@vuedir/components/App'),
                children: [
                    { path: 'create', name: routes.ROUTE_CREATE, component: () => import(/* webpackChunkName: "Tiles" */ '@vuedir/components/Tiles') },
                    { path: 'modify/:id', name: routes.ROUTE_MODIFY, component: () => import(/* webpackChunkName: "Tiles" */ '@vuedir/components/Tiles') },
                ],
            },
        ];
        const router = createRouter({
            history: createWebHistory(element.getAttribute('data-app-tile-builder')),
            routes: routerRoutes,
        });
        const store = createStore({ modules });
        const props = JSON.parse(element.getAttribute('data-app-props'));
        const locale = props.locale || locales.LOCALE_DEFAULT;
        const currencyCode = props.currencyCode || locales.CURRENCY_CODE_DEFAULT;
        const currencyFilter = new CurrencyFilter(locale, currencyCode);
        const triggerBuilderRoute = (event) => {
            const target = event.target;
            const match = matches(target, '[data-app-launch-builder]');
            if (match) {
                let href = match.getAttribute('href');
                // support the use of valid urls
                if (href.startsWith('/art')) {
                    href = href.replace('/art', '');
                }
                event.preventDefault();
                router.push(href);
            }
        };
        let app = createApp({
            el: element,
            mounted: function () {
                // Make it possible for regular buttons to trigger the router.
                if (!lazyHref) {
                    document.addEventListener('click', triggerBuilderRoute);
                }
            },
            unmounted: function () {
                // Remove obsolete event listener
                if (!lazyHref) {
                    document.removeEventListener('click', triggerBuilderRoute);
                }
            },
            render: () => {
                const component = resolveComponent('router-view');
                return h(component);
            },
        }, props);
        app.config.globalProperties.$filters = {
            currency(value) {
                return currencyFilter.format(value);
            }
        };
        app.use(router);
        app.use(store);
        app.use(i18n);
        setTurbolinkListener(app);
        app.mount(element);
        if (lazyHref) {
            router.push(lazyHref);
        }
    }));
});
// A global function that will unmount the vue app and remove the turbo
// event listener that calls it.
function setTurbolinkListener(app) {
    function unmountApp() {
        app.unmount();
        document.removeEventListener('turbo:before-cache', unmountApp);
    }
    document.addEventListener('turbo:before-cache', unmountApp);
}
function mountAndReplace(app, element) {
    var _a;
    const fragment = document.createDocumentFragment();
    // Mount app to the fragment
    app.mount(fragment);
    // Replace mounting element with fragment
    (_a = element.parentNode) === null || _a === void 0 ? void 0 : _a.replaceChild(fragment, element);
}
